import type {AdPixel} from "./adPixels";
import type {AdPurchaseConversion} from "./AdsApi";
import {metaPixelReportsHere, metaPixelRunning, trackMetaPurchase} from "./metaPixel";
import {metaPurchaseOf, purchaseVerdict, redditPurchaseOf, rememberReportedPurchase, settlingPurchasePixels,
	unreportedPurchases} from "./purchaseConversions";
import {redditPixelReportsHere, redditPixelRunning, trackRedditPurchase} from "./redditPixel";

/**
 * The pixels a recorded purchase is reported to, and the round that reports it
 * (kitecorp/transferify-api-service#1241, Meta added by kitecorp/transferify#375) — React-free.
 *
 * The rules ("report only what this pixel was not told", "remember only what actually left", "when is a
 * settling purchase done") are `./purchaseConversions`'; which pixel may send is each pixel's own. What
 * lives here is the list of pixels, so `usePurchaseConversions` names none of them and a third pixel is
 * one entry rather than a second copy of the hook's loop.
 */
interface PurchaseReporter {
	pixel: AdPixel;
	/** Whether the pixel is running in this page load at all. */
	running: () => boolean;
	/** Whether an event sent now would reach it: running, and this page is inside its scope. */
	readyHere: () => boolean;
	/** Sends one purchase; answers whether it left. */
	report: (purchase: AdPurchaseConversion) => boolean;
}

const PURCHASE_REPORTERS: readonly PurchaseReporter[] = [
	{pixel: "reddit", running: redditPixelRunning, readyHere: redditPixelReportsHere,
		report: (purchase) => trackRedditPurchase(redditPurchaseOf(purchase))},
	{pixel: "meta", running: metaPixelRunning, readyHere: metaPixelReportsHere,
		report: (purchase) => trackMetaPurchase(metaPurchaseOf(purchase))},
];

/** The pixels the settling purchase may reach at all (`purchaseConversions.settlingPurchasePixels`). */
function coveredReporters(): PurchaseReporter[] {
	const covered = settlingPurchasePixels();
	return PURCHASE_REPORTERS.filter((reporter) => covered.includes(reporter.pixel));
}

/** Whether any pixel could report the settling purchase on this page, which is what an api read is worth. */
export function anyPurchasePixelReadyHere(): boolean {
	return coveredReporters().some((reporter) => reporter.readyHere());
}

/**
 * One round: every pixel the settling purchase may reach and that may report on this page is told every
 * recorded purchase it was not told
 * yet, each remembered per pixel and immediately (a tab closed between two events must not leave the
 * first able to fire again), and ONLY when the pixel says the event left — a withdrawal or a navigation
 * while the api read was in flight closes a pixel's gate, and remembering a dropped purchase would lose
 * it for good.
 *
 * @returns what to do with the settling mark next ({@link purchaseVerdict})
 */
export function reportRecordedPurchases(recorded: readonly AdPurchaseConversion[]):
	ReturnType<typeof purchaseVerdict> {
	const covered = coveredReporters();
	const ready = covered.filter((reporter) => reporter.readyHere());
	ready.forEach((reporter) => {
		unreportedPurchases(recorded, reporter.pixel).forEach((purchase) => {
			if (reporter.report(purchase)) {
				rememberReportedPurchase(purchase.conversionId, reporter.pixel);
			}
		});
	});
	return purchaseVerdict(recorded, {
		running: covered.filter((reporter) => reporter.running()).map((reporter) => reporter.pixel),
		ready: ready.map((reporter) => reporter.pixel),
	});
}
